/**
 * LenderOfferSelection - V3 Journey Component
 * 
 * Replaces LoanDetails component with interactive lender offer selection
 * - Fetches 3x3 matrix from API
 * - Allows user to select loan amount, duration, and repayment type
 * - Saves selection to backend
 * - Integrates with v3 journey orchestrator pattern
 */

import React, { useState, useEffect } from 'react';
import { Slider } from '@/components/ui/slider';
import { Lock, AlertCircle } from 'lucide-react';
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';

interface LenderOfferSelectionProps {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
}

type DurationType = '90 Days' | '180 Days' | '360 Days';
type InstallmentType = 'EDI' | 'EWI' | 'EMI';

// API Response structure
interface LenderOffersResponse {
  rows: string[];
  columns: string[];
  data: (([number, number] | null)[])[]; 
}

interface OfferData {
  maxAmount: number;
  interestRate: number;
}

const LenderOfferSelection: React.FC<LenderOfferSelectionProps> = ({ 
  value, 
  onChange, 
  disabled = false,
  registerSubmit 
}) => {
  // State management
  const [loanAmount, setLoanAmount] = useState<number>(100000);
  const [duration, setDuration] = useState<DurationType>('90 Days');
  const [installmentType, setInstallmentType] = useState<InstallmentType>('EMI');
  const [offersData, setOffersData] = useState<LenderOffersResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Fetch offers from API
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/api/lender/offers/get/`);
        
        if (response.data.success) {
          const apiData = response.data.data;
          setOffersData(apiData);
          
          // Set initial duration and installment type based on available data
          if (apiData.rows && apiData.rows.length > 0) {
            setDuration(apiData.rows[0] as DurationType);
          }
          if (apiData.columns && apiData.columns.length > 0) {
            setInstallmentType(apiData.columns[0] as InstallmentType);
          }
        } else {
          setError('Failed to fetch lender offers');
        }
      } catch (err: any) {
        console.error('Error fetching lender offers:', err);
        setError(err.response?.data?.message || 'Failed to load offers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  // Get current offer based on selected duration and installment type
  const getCurrentOffer = (): OfferData | null => {
    if (!offersData) return null;

    const rowIndex = offersData.rows.indexOf(duration);
    const colIndex = offersData.columns.indexOf(installmentType);

    if (rowIndex === -1 || colIndex === -1) return null;

    const offerCell = offersData.data[rowIndex]?.[colIndex];
    if (!offerCell) return null;

    return {
      maxAmount: offerCell[0],
      interestRate: offerCell[1]
    };
  };

  const currentOffer = getCurrentOffer();

  // Configuration based on current offer
  const MIN_AMOUNT = 0;
  const MAX_AMOUNT = currentOffer?.maxAmount || 1000000;
  const INTEREST_RATE = currentOffer?.interestRate || 6.25;
  const IS_APPROVED = currentOffer !== null;

  // Check if duration is available
  const isDurationAvailable = (dur: DurationType): boolean => {
    if (!offersData) return false;
    return offersData.rows.includes(dur);
  };

  // Check if installment type is available for current duration
  const isInstallmentTypeAvailable = (type: InstallmentType): boolean => {
    if (!offersData) return false;
    
    const rowIndex = offersData.rows.indexOf(duration);
    const colIndex = offersData.columns.indexOf(type);

    if (rowIndex === -1 || colIndex === -1) return false;

    const offerCell = offersData.data[rowIndex]?.[colIndex];
    return offerCell !== null && offerCell !== undefined;
  };

  // Check if installment type exists in API response
  const isInstallmentTypeInResponse = (type: InstallmentType): boolean => {
    if (!offersData) return false;
    return offersData.columns.includes(type);
  };

  // Auto-set loan amount to max when plan changes
  useEffect(() => {
    if (currentOffer) {
      setLoanAmount(currentOffer.maxAmount);
    }
  }, [duration, installmentType, currentOffer?.maxAmount]);

  // Auto-adjust installment type if current one is not available
  useEffect(() => {
    if (offersData && !isInstallmentTypeAvailable(installmentType)) {
      const availableType = offersData.columns.find(col =>
        isInstallmentTypeAvailable(col as InstallmentType)
      );
      if (availableType) {
        setInstallmentType(availableType as InstallmentType);
      }
    }
  }, [duration, offersData]);

  // Convert duration string to days
  const getDurationInDays = (dur: DurationType): number => {
    if (dur === '90 Days') return 90;
    if (dur === '180 Days') return 180;
    if (dur === '360 Days') return 360;
    return 90;
  };

  // Calculate installment details
  const calculateInstallments = () => {
    const principal = loanAmount;
    const ratePerAnnum = INTEREST_RATE / 100;
    const days = getDurationInDays(duration);
    const months = Math.round(days / 30);

    // Simple interest calculation
    const interest = (principal * ratePerAnnum * months) / 12;
    const totalRepayable = principal + interest;

    let installmentAmount = 0;
    let installmentCount = 0;
    let installmentLabel = '';

    if (installmentType === 'EMI') {
      installmentCount = months;
      installmentAmount = totalRepayable / months;
      installmentLabel = 'Monthly Instalment';
    } else if (installmentType === 'EWI') {
      installmentCount = Math.ceil(days / 7);
      installmentAmount = totalRepayable / installmentCount;
      installmentLabel = 'Weekly Instalment';
    } else if (installmentType === 'EDI') {
      installmentCount = days;
      installmentAmount = totalRepayable / installmentCount;
      installmentLabel = 'Daily Instalment';
    }

    return {
      principal,
      interest,
      totalRepayable,
      installmentAmount,
      installmentCount,
      installmentLabel
    };
  };

  const installmentData = calculateInstallments();

  // Format currency
  const formatCurrency = (amount: number) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(2)}K`;
    }
    return `₹${amount.toFixed(2)}`;
  };

  const formatCurrencyFull = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Submit handler for orchestrator
  const handleSubmit = async (): Promise<boolean> => {
    if (!IS_APPROVED) {
      alert('Please select an approved loan combination');
      return false;
    }

    try {
      setSubmitting(true);

      // Save selection to backend
      const tenureDays = getDurationInDays(duration);
      
      const response = await axios.post(`${API_BASE_URL}/api/lender/offers/select/`, {
        tenure: duration,
        tenure_days: tenureDays,
        repayment_type: installmentType,
        loan_amount: loanAmount,
        interest_rate: INTEREST_RATE,
        max_amount: MAX_AMOUNT,
        installment_amount: installmentData.installmentAmount,
        total_repayable: installmentData.totalRepayable,
        interest_amount: installmentData.interest
      });

      if (response.data.success) {
        console.log('Offer selected successfully:', response.data.data);
        
        // Notify parent component
        if (onChange) {
          onChange({
            loan_amount: loanAmount,
            loan_tenure: tenureDays,
            repayment_method: installmentType,
            interest_rate: INTEREST_RATE
          });
        }
        
        return true;
      } else {
        alert('Failed to save selection. Please try again.');
        return false;
      }
    } catch (err: any) {
      console.error('Error saving selection:', err);
      alert('Failed to save selection. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Register submit handler with orchestrator
  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(handleSubmit);
    }
  }, [registerSubmit, loanAmount, duration, installmentType, IS_APPROVED]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loader-circle mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lender offers...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !offersData) {
    return (
      <div className="py-6">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-red-800 font-bold text-lg mb-2">Unable to Load Offers</h3>
          <p className="text-red-600 text-sm mb-4">{error || 'No offers available'}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-intellend-green text-center mb-4">
        Select Your Loan Plan
      </h2>

      {/* Loan Amount Section */}
      <div className="bg-white rounded-2xl p-6 shadow-md">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-gray-700 font-medium text-base">Loan Amount</h3>
          <div className="text-indigo-600 font-bold text-xl">
            {formatCurrency(loanAmount)}
          </div>
        </div>

        {/* Offer Info */}
        {currentOffer && (
          <div className="mb-3 text-xs">
            <span className={`px-2 py-1 rounded-full ${IS_APPROVED ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {IS_APPROVED ? '✓ Approved' : '✗ Not Approved'}
            </span>
            <span className="ml-2 text-gray-500">
              Max: {formatCurrency(MAX_AMOUNT)} • Rate: {INTEREST_RATE}% p.a.
            </span>
          </div>
        )}

        {!currentOffer && (
          <div className="mb-3 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">
            No offer available for this combination
          </div>
        )}

        {/* Slider */}
        <div className="mb-4">
          <Slider
            value={[loanAmount]}
            onValueChange={(values) => setLoanAmount(values[0])}
            min={MIN_AMOUNT}
            max={MAX_AMOUNT}
            step={10000}
            className="w-full"
            disabled={disabled || !IS_APPROVED}
          />
        </div>

        {/* Min/Max labels */}
        <div className="flex justify-between text-xs text-gray-400">
          <span>₹0</span>
          <span>{formatCurrency(MAX_AMOUNT)}</span>
        </div>
      </div>

      {/* Loan Duration Section */}
      <div className="bg-white rounded-2xl p-6 shadow-md">
        <h3 className="text-gray-700 font-medium text-base mb-4">Loan Duration</h3>
        
        <div className="grid grid-cols-3 gap-3">
          {(['90 Days', '180 Days', '360 Days'] as DurationType[]).map((dur) => {
            const available = isDurationAvailable(dur);
            const isSelected = duration === dur;

            return (
              <button
                key={dur}
                onClick={() => available && !disabled && setDuration(dur)}
                disabled={!available || disabled}
                className={`
                  relative py-4 px-3 rounded-xl font-semibold text-center transition-all
                  ${isSelected && available
                    ? 'bg-indigo-600 text-white shadow-lg scale-105'
                    : available
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                  }
                `}
              >
                {!available && (
                  <Lock className="w-4 h-4 absolute top-2 right-2 text-gray-400" />
                )}
                <div className="text-lg font-bold">{dur}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Installment Type Tabs & Details */}
      <div className="bg-white rounded-2xl p-6 shadow-md">
        {/* Tabs - Always show all 3 types */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {(['EDI', 'EWI', 'EMI'] as InstallmentType[]).map((type) => {
            const inResponse = isInstallmentTypeInResponse(type);
            const available = inResponse && isInstallmentTypeAvailable(type);
            const isActive = installmentType === type;
            const colorClass = type === 'EMI' ? 'indigo' : type === 'EWI' ? 'cyan' : 'teal';

            return (
              <button
                key={type}
                onClick={() => available && !disabled && setInstallmentType(type)}
                disabled={!available || disabled}
                className={`flex-1 pb-3 text-sm font-medium transition-all relative ${
                  isActive 
                    ? `text-${colorClass}-600` 
                    : available
                    ? 'text-gray-400 hover:text-gray-600'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                {type}
                {!available && (
                  <Lock className="w-3 h-3 absolute top-1 right-1 text-gray-300" />
                )}
                {isActive && available && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${
                    type === 'EMI' ? 'bg-indigo-600' : type === 'EWI' ? 'bg-cyan-600' : 'bg-teal-600'
                  }`} />
                )}
              </button>
            );
          })}
        </div>

        {/* Installment Display */}
        <div className="text-center mb-6">
          <div className={`text-xs font-medium mb-2 ${
            installmentType === 'EMI' ? 'text-indigo-600' : installmentType === 'EWI' ? 'text-cyan-600' : 'text-teal-600'
          }`}>
            {installmentData.installmentLabel}
          </div>
          <div className="text-gray-500 text-sm mb-2">
            Per {installmentType === 'EMI' ? 'Month' : installmentType === 'EWI' ? 'Week' : 'Day'}
          </div>
          <div className={`text-4xl font-bold mb-2 ${
            installmentType === 'EMI' ? 'text-indigo-600' : installmentType === 'EWI' ? 'text-cyan-600' : 'text-teal-600'
          }`}>
            {formatCurrencyFull(installmentData.installmentAmount)}
          </div>
          <div className="text-gray-400 text-sm">
            × {installmentData.installmentCount} instalments
          </div>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Loan Amount</div>
            <div className="font-bold text-gray-800 text-sm">
              {formatCurrency(installmentData.principal)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Interest ({INTEREST_RATE}% p.a.)</div>
            <div className="font-bold text-amber-600 text-sm">
              {formatCurrency(installmentData.interest)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Total Repayable</div>
            <div className="font-bold text-gray-800 text-sm">
              {formatCurrency(installmentData.totalRepayable)}
            </div>
          </div>
        </div>
      </div>

      {submitting && (
        <div className="text-center text-gray-600 text-sm">
          Saving your selection...
        </div>
      )}
    </div>
  );
};

export default LenderOfferSelection;
